//-------------------------------------------------------------------------------------
//
//	GfxContainer
//
//		
//
//-------------------------------------------------------------------------------------

// To Do
// -----
// fix close button style in two-column display mode
// add number display option (instead of number entry)
// add GfxApp config: grid, show info, filename
// fix issue with trackpad to zoom on Safari
// add 'keeps bounds on screen' checkbox
// add interval timer to do animations
// test color apps (e.g., fibonacci)
// add units (mm, inches, pixels)
// add optional local storage (does it belong in container or in parent?)
// track changed settings (for undo/redo)
//
// Future
// ------
// implement gfxApp thumbnails
// add toolbar which is populated similar to controls (standard tool btns, custom tool btns) (provide image URL)
// add undo/redo
// 


/*	
	gfxContainer-controls:
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    
    
	gfxContainer-controlGroup:
    margin: 10px;
    padding: 5px;
    width: 215px;
*/

//-------------------------------------------------------------------------------------
//	Imports
//-------------------------------------------------------------------------------------
import { Transform, Projection3D } from "../../VectorUtilsJS/src/VectorUtilLib.js";
import { Math3D } from "../../VectorUtilsJS/src/VectorUtilLib.js";
import { GfxDrawing, GfxPath } from "../../GfxContainer/src/GfxDrawing.js";
import { GfxRender } from "../../GfxContainer/src/GfxRender.js";


//=====================================================================================
//
//	GfxContainer
//
//=====================================================================================

//-------------------------------------------------------------------------------------
//	Constructor
//
//	config
//		handleDataLoaded: called with a gfxApp name and settings when a file is opened
//		reportError: called when an error has occurred
//	containerDiv: location to install GfxContainer
//	addCloseButton: if true add a "Close" button; if string use as close button label
//	closeCallback: called if close button is clicked
//		
//-------------------------------------------------------------------------------------
function GfxContainer(config, containerDiv)
{
	this.config = config;
	this.containerDiv = containerDiv;
	this.fileNamespace = JSON.parse(JSON.stringify(GfxContainer.defaultFileNamespace));
	
	this.InternalInitialize();
	this.BuildContainer();
}

//-------------------------------------------------------------------------------------
//	Internal Initialize
//-------------------------------------------------------------------------------------
GfxContainer.prototype.InternalInitialize = function()
{
	this.gfxApp = undefined;
	this.infoElement = undefined;
	this.filenameField = undefined;
	this.viewTransform = new Transform();
	this.viewProjection = new Projection3D(); // 3D
	this.settings = Object.assign({}, GfxContainer.defaultSettings);
	this.mouseTracking = {};
	this.appConfig = Object.assign({}, GfxContainer.defaultAppConfig);
	this.twoColumn = false;
	this.gfxAppStatus = undefined;
}


//-------------------------------------------------------------------------------------
//	Default File Namespace
//-------------------------------------------------------------------------------------
GfxContainer.defaultFileNamespace = 
	{
		companyName: "thevaportrail",
		appName:     "appName",
		dataName:    "dataName",
		dataId:      "dataId",
	}

//-------------------------------------------------------------------------------------
//	Default AppConfig
//-------------------------------------------------------------------------------------
GfxContainer.defaultAppConfig = 
	{
		origin:		"center",
		ypositive:	"up",
		filename:	"gfxDesign"
	}

//-------------------------------------------------------------------------------------
//	Default settings
//-------------------------------------------------------------------------------------
GfxContainer.defaultSettings = 
	{
		showGrid:		true,
		showOrigin:		false,
		scale:			1.0,
		origin:			"topleft", // "topleft", "center", "bottomleft"
		yscale:			1.0,	// 1 or -1, according to appConfig.ypositive setting
		gridSpacing:	50.0,
		filename:		"gfxAppSettings",
		rotationX:		0,
		rotationY:		0,
		rotationZ:		0
	}

//-------------------------------------------------------------------------------------
//	Controls and Control Groups
//-------------------------------------------------------------------------------------
GfxContainer.view3DControls =
	[
		{ label: "Rotation X", 		type: "number",		prop: "rotationX",	min:0, max: 360, step:1, entry:"yes" 	},
		{ label: "Rotation Y", 		type: "number",		prop: "rotationY",	min:0, max: 360, step:1, entry:"yes" 	},
		{ label: "Rotation Z", 		type: "number",		prop: "rotationZ",	min:0, max: 360, step:1, entry:"yes" 	},
	];

GfxContainer.miscControls =
	[
		{ label: "Show Grid",			type: "boolean",	prop: "showGrid"			},
		{ label: "Show Origin",			type: "boolean",	prop: "showOrigin"			},
		{ label: "Show Common Sizes",	type: "boolean",	prop: "showPaper"			},
		{ label: "Zoom to Fit",			type: "button",		action: "gfxZoomToFit" 		},
		{ label: "Zoom 100%",			type: "button",		action: "gfxZoom100" 		},
	];

GfxContainer.fileControls =
	[
		{ label: "Name:",		type: "text",		prop: "filename"	},
		{ label: "Save as SVG",	type: "button",		action: "gfxExportSVG" 		},
		{ label: "Open File",	type: "file",		action: "gfxOpenFile"		}
	];

GfxContainer.controlGroups = 
	[
		{ label: "Misc",			controls: GfxContainer.miscControls, owner: "container"	},
		{ label: "Save and Load",	controls: GfxContainer.fileControls, owner: "container"	}
	];

GfxContainer.controlGroups3D = 
	[
		{ label: "3D",				controls: GfxContainer.view3DControls, owner: "container", update3D:"yes"	}
	];

//-------------------------------------------------------------------------------------
//	Reset
//-------------------------------------------------------------------------------------
GfxContainer.prototype.Reset = function()
{
	this.InternalInitialize();
	this.AdjustTwoColumn("disable");
	this.RemoveUI();
}

//-------------------------------------------------------------------------------------
//	Close
//-------------------------------------------------------------------------------------
GfxContainer.prototype.Close = function()
{
	this.Reset();
}

//-------------------------------------------------------------------------------------
//	Set Gfx App
//-------------------------------------------------------------------------------------
GfxContainer.prototype.SetGfxApp = function(gfxApp)
{
	this.gfxApp = gfxApp;

	// Update the appConfig with any config options provided by the app
	let appConfig = this.gfxApp.GetConfig();
	Object.assign(this.appConfig, appConfig);
	
	this.settings.origin = this.appConfig.origin;
	this.settings.filename = this.appConfig.filename;
}


//-------------------------------------------------------------------------------------
//	Generate
//-------------------------------------------------------------------------------------
GfxContainer.prototype.Generate = function()
{
	if (this.gfxApp != undefined)
	{
		this.gfxApp.Generate();
		
		this.gfxAppStatus = this.gfxApp.GetStatus();
	}
}


//-------------------------------------------------------------------------------------
//	Generate
//-------------------------------------------------------------------------------------
GfxContainer.prototype.UpdateInfo = function()
{
	if (this.infoElement != undefined)
	{
		var infoStr = "..."

		if (this.gfxApp != undefined)
		{
			var dim = this.gfxApp.GetDimensions();
			if (dim != undefined)
			{
				var w = dim.x.toFixed(2);
				var h = dim.y.toFixed(2);
				infoStr = "Size: " + w + ", " + h + "mm";

				var iWi = (w/25.4).toFixed(2);
				var iHi = (h/25.4).toFixed(2);
	
				infoStr += " (" + iWi + " x " + iHi + " in)";
			}
			
			var info = this.gfxApp.GetInfo();
			if (info != undefined)
			{
				infoStr += "<br>" + info;
			}
		}

		this.infoElement.innerHTML = infoStr;
	}
}


//-------------------------------------------------------------------------------------
//	Render
//-------------------------------------------------------------------------------------
GfxContainer.prototype.Render = function()
{
	if (this.canvas != undefined)
	{
		var context = this.canvas.getContext("2d");
	
		context.clearRect(0, 0, this.canvas.width, this.canvas.height);
		
		// Render the container first, so it is behind the gfx app
		this.ContainerRender(context);
		
		// Render the gfx app
		this.AppRender(context);
		
		// Display any status provided by the gfxApp
		this.UpdateStatus();
	}
}

//-------------------------------------------------------------------------------------
//	Update Status
//-------------------------------------------------------------------------------------
GfxContainer.prototype.UpdateStatus = function()
{
	if (this.statusArea != undefined)
	{
		var errStr = this.gfxAppStatus != undefined ? this.gfxAppStatus.error : undefined;
		var wrnStr = this.gfxAppStatus != undefined ? this.gfxAppStatus.warning : undefined;
		var msgStr = this.gfxAppStatus != undefined ? this.gfxAppStatus.msg : undefined;
		
		if (errStr == undefined && wrnStr == undefined && msgStr == undefined)
		{
			this.statusArea.classList.add("gfxContainer-hideElement");
		}
		else
		{
			this.statusArea.classList.remove("gfxContainer-hideElement");
			
			var str = "";
			if (errStr != undefined)
				str += "Error: " + errStr + "<br>";
			
			if (wrnStr != undefined)
				str += "Warning: " + wrnStr + "<br>";
			
			if (msgStr != undefined)
				str += msgStr + "<br>";
				
			this.statusArea.innerHTML = str;
		}
	}
}

//-------------------------------------------------------------------------------------
//	App Render
//-------------------------------------------------------------------------------------
GfxContainer.prototype.AppRender = function(context)
{
	if (this.gfxApp != undefined)
	{
		if (this.appConfig.display3D)
			this.gfxApp.RenderToContext(context, this.viewTransform, this.viewProjection);
		else
			this.gfxApp.RenderToContext(context, this.viewTransform);
	}
}

//-------------------------------------------------------------------------------------
//	Container Render
//-------------------------------------------------------------------------------------
GfxContainer.prototype.ContainerRender = function(context)
{
	if (this.settings.showGrid)
		this.RenderGrid(context)

	if (this.settings.showOrigin)
		this.RenderOrigin(context)

	if (this.settings.showPaper)
		this.RenderPaperSizes(context)
}

//-------------------------------------------------------------------------------------
//	Render Grid
//-------------------------------------------------------------------------------------
GfxContainer.prototype.RenderGrid = function(context)
{
	var spacing = this.settings.gridSpacing;
	var minPt = {x: 0, y: 0};
	var maxPt = {x: context.canvas.width, y: context.canvas.height};
	
	// Convert the canvas min/max to view min/max
	minPt = this.viewTransform.reverse_xfrm(minPt);
	maxPt = this.viewTransform.reverse_xfrm(maxPt);
	
	// Swap the y min and max when the y-axis is inverted
	if (maxPt.y < minPt.y)
	{
		var y = maxPt.y;
		maxPt.y = minPt.y;
		minPt.y = y;
	}	
	
	context.strokeStyle = "rgba(192, 192, 128, 0.2)";
	context.lineWidth = 1;
	
	// Perform a modulu on the starting x and y values in the iterators so that
	// the lines always cross at (0,0)
	
	context.beginPath();
	for (var x = spacing * Math.floor(minPt.x / spacing); x < maxPt.x; x += spacing)
	{
		GfxRender.MoveTo(context, {x:x, y:minPt.y}, this.viewTransform);
		GfxRender.LineTo(context, {x:x, y:maxPt.y}, this.viewTransform);
	}
	context.stroke();


	context.beginPath();
	for (var y = spacing * Math.floor(minPt.y / spacing); y < maxPt.y; y += spacing)
	{
		GfxRender.MoveTo(context, {x:minPt.x, y:y}, this.viewTransform);
		GfxRender.LineTo(context, {x:maxPt.x, y:y}, this.viewTransform);
	}
	context.stroke();
}

//-------------------------------------------------------------------------------------
//	Render Paper Sizes
//-------------------------------------------------------------------------------------
GfxContainer.prototype.RenderPaperSizes = function(context)
{
	let inToMM = 25.4;
	let lineWidth = 0.5;
	
	let paperSizes = 
	[
		{x: 8.5,  y: 11,  units:"in", name:"Letter" },
	//	{x: 297,  y: 420, units:"mm", name:"A3" },
		{x: 210,  y: 297, units:"mm", name:"A4" },
		{x: 12,   y: 12,  units:"in", name:"12\"x12\"" }
	]
	
	var bounds = (this.gfxApp != undefined) ? this.gfxApp.GetBounds() : undefined;
	var corner = (bounds != undefined) ? bounds.min : {x: 0, y: 0};
	
	
	lineWidth = this.viewTransform.scaleNum(lineWidth);
	corner = this.viewTransform.xfrm(corner); 
	
	context.strokeStyle = "rgba(192, 192, 255, 1.0)";
	context.fillStyle = "rgba(0, 0, 255, 0.5)";
	context.lineWidth = lineWidth;
	context.font = '16px sans-serif';
	
	let vInset = lineWidth + 2;
	let hInset = lineWidth + 2;
	
	for (var i = 0; i < paperSizes.length; i++)
	{
		let paper = paperSizes[i];
		let scale = (paper.units == "in") ? inToMM : 1.0;
		let width  = this.viewTransform.scaleNum(paper.x * scale);
		let height = this.viewTransform.scaleNum(paper.y * scale);
		
		
		context.strokeRect(corner.x, corner.y, width, height);
		
		if (paper.name != undefined)
		{
			var m = context.measureText(paper.name);
			context.fillText(paper.name, corner.x + width - m.width - hInset, corner.y + height - vInset);
		}
	}

	context.fillStyle = "black";
}

//-------------------------------------------------------------------------------------
//	Render Origin
//-------------------------------------------------------------------------------------
GfxContainer.prototype.RenderOrigin = function(context)
{
	var origin = {x: 0, y: 0};
	var axisLen = 20;
	
	// Convert the canvas axisLen to view axisLen
	axisLen = this.viewTransform.reverse_scaleNum(axisLen);
	
	context.lineWidth = 1;
	
	context.strokeStyle = "red";
	context.beginPath();
	GfxRender.MoveTo(context, origin, this.viewTransform);
	GfxRender.LineTo(context, {x:axisLen, y:0}, this.viewTransform);
	context.stroke();


	context.strokeStyle = "blue";
	context.beginPath();
	GfxRender.MoveTo(context, origin, this.viewTransform);
	GfxRender.LineTo(context, {x:0, y:axisLen}, this.viewTransform);
	context.stroke();
}

//-------------------------------------------------------------------------------------
//	Save As
//-------------------------------------------------------------------------------------
GfxContainer.prototype.SaveAs = function(fileType /* svg, txt, dxf, png */, fileData, documentName)
{
	//convert svg source to URI data scheme.
	var urlPrefix = "";
	
	if (fileType === "svg")
		urlPrefix = "data:image/svg+xml;charset=utf-8,";
	else if (fileType === "txt")
		urlPrefix = "data:text/plain;charset=utf-8,";
	else if (fileType === "dxf")
		urlPrefix = "data:text/plain;charset=utf-8,";
	else if (fileType === "png")
		urlPrefix = undefined; // Data is already prepared
	//else
	//	Debug_assert("GfxContainer.prototype.SaveAs: Unknown fileType:" + fileType);
	
	var url = undefined;		
	if (urlPrefix != undefined)
		url = urlPrefix + encodeURIComponent(fileData);
	else
		url = fileData;

	// Get the document name from the page
	var docName = documentName;
	docName += "." + fileType;
	
	// Create an element to perform the download and then clean-up
	var a = document.createElement('a');
	a.setAttribute('href', url);
	a.setAttribute('download', docName);
	a.style.display = 'none';
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
}

//-------------------------------------------------------------------------------------
//	Save As SVG
//-------------------------------------------------------------------------------------
GfxContainer.prototype.SaveAsSVG = function()
{
	var svg = undefined;

	// (I need to think about where and when the namespace should be set and how it is used)
	GfxRender.SetSVGNamespace(this.fileNamespace.companyName, this.fileNamespace.appName, this.fileNamespace.dataName, this.fileNamespace.dataId);
	
	if (this.gfxApp != undefined)
	{
		var gfxAppSettings = this.gfxApp.GetSettingsCopy();
		var gfxAppName = this.gfxApp.GetName();
		var gfxAppId = this.gfxApp.GetAppId();
		
		var appData = {name:gfxAppName, appId:gfxAppId, settings:gfxAppSettings};
		svg = this.gfxApp.RenderToSVG(appData);
	}

	var filename = this.settings.filename;
	if (filename == undefined || filename.length == 0)
		filename = "untitled";

	if (svg != undefined)
		GfxContainer.prototype.SaveAs("svg", svg, filename);
}

//-------------------------------------------------------------------------------------
//	Save As Blob (this needs to be cleaned-up. it was copied from another file.)
//-------------------------------------------------------------------------------------
GfxContainer.prototype.SaveAsBlob = function(fileType /* svg, dxf */, fileData, documentName)
{
	try {
		var docName = documentName + "." + fileType;
		var blobType = "";

		if (fileType === "svg")
			blobType = "image/svg+xml;charset=utf-8,";
		else if (fileType === "dxf")
			blobType = "text/plain;charset=utf-8,";
		else
			console.log("GfxContainer.prototype.SaveAsBlob: Unknown fileType:" + fileType);

		var blob = new Blob([fileData], {type: blobType});
		var url = URL.createObjectURL(blob);

		// Handler to clean-up the URL after the download
		var clickHandler = function()
		{
			requestAnimationFrame(function() {
				URL.revokeObjectURL(element.href);
			})

			a.removeAttribute('href')
			a.removeEventListener('click', clickHandler)
		};

		// Create an element to perform the download and then clean-up
		var a = document.createElement('a');
		a.setAttribute('href', url);
		a.setAttribute('download', docName);
		a.style.display = 'none';
		document.body.appendChild(a);
		a.click();
		a.addEventListener('click', clickHandler);
		document.body.removeChild(a);
	}
	catch (err) {
		console.log("GfxContainer.prototype.SaveAsBlob");
		console.log(err);
	}
}

//-------------------------------------------------------------------------------------
//	Handle Loaded Data
//-------------------------------------------------------------------------------------
GfxContainer.prototype.HandleLoadedData = function(loadedData)
{
	if (loadedData != undefined)
	{
		if (this.config.handleDataLoaded != undefined)
			this.config.handleDataLoaded(loadedData);
	}
}

//-------------------------------------------------------------------------------------
//	Load SVG
//-------------------------------------------------------------------------------------
GfxContainer.prototype.LoadSVG = function(evt, filename)
{
	// Data that may be loaded from the document
	var loadedAppData = undefined;
	
	// Get the contents from the (file) event
	var contents = evt.target.result;

	// Parse the contents
	var parser = new DOMParser();
	var xmlDoc = parser.parseFromString(contents, "image/svg+xml");

	// Get the data, if it exists
	var appDataElement = xmlDoc.getElementById(this.fileNamespace.dataId);
	var appDataStr = undefined;
	
	if (appDataElement != undefined)
		appDataStr = appDataElement.textContent;
		
	if (appDataStr != undefined)
		loadedAppData = JSON.parse(appDataStr);
	
	if (loadedAppData != undefined)
		this.HandleLoadedData(loadedAppData);
	else
		this.ReportError("Unable to read data from SVG file '" + filename + "'");
}

//-------------------------------------------------------------------------------------
//	Load TXT
//-------------------------------------------------------------------------------------
GfxContainer.prototype.LoadTXT = function(evt, filename)
{
	// Data that may be loaded from the document
	var loadedAppData = undefined;
	
	// Get the contents from the (file) event
	var contents = evt.target.result;

	// Parse the contents
	var loadedAppData = JSON.parse(contents);
	
	GfxContainer.prototype.HandleLoadedData(loadedAppData);
}

//-------------------------------------------------------------------------------------
//	Load DXF
//-------------------------------------------------------------------------------------
GfxContainer.prototype.LoadDXF = function(evt, filename)
{
	// Data that may be loaded from the document
	var loadedAppData = undefined;
	
	// Get the contents from the (file) event
	var contents = evt.target.result;

	// By lines
	var lines = contents.split('\n');
	var contents = "";
	var prefix = "[AppData]";
	
	for (var line = 0; line < lines.length; line++)
	{
		if (lines[line].includes(prefix, 0))
			contents += lines[line].substring(prefix.length);
	}
	
	// Parse the contents
	loadedAppData = JSON.parse(contents);
	
	if (loadedAppData != undefined)
		GfxContainer.prototype.HandleLoadedData(loadedAppData);
}

//-------------------------------------------------------------------------------------
//	Regular expressions for matching file types
//-------------------------------------------------------------------------------------
var REGEX_svg = /\.svg$/i;
var REGEX_txt = /\.txt$/i;
var REGEX_dxf = /\.dxf$/i;
var REGEX_json = /\.json$/i;

//-------------------------------------------------------------------------------------
//	Load Completed
//-------------------------------------------------------------------------------------
GfxContainer.prototype.LoadCompleted = function(evt, filename)
{
	if (REGEX_svg.test(filename))
		this.LoadSVG(evt, filename);
		
	else if (REGEX_txt.test(filename) || REGEX_json.test(filename))
		this.LoadTXT(evt, filename);
		
	else if (REGEX_dxf.test(filename))
		this.LoadDXF(evt, filename);
		
	else
		this.ReportError("Unexpected file type after loading file: " + filename);
}

//-------------------------------------------------------------------------------------
//	Load File
//-------------------------------------------------------------------------------------
GfxContainer.prototype.LoadFile = function(e, loadInfo)
{
	var file = e.target.files[0];
	if (file != undefined)
	{
		if (REGEX_svg.test(file.name) || REGEX_txt.test(file.name) || REGEX_dxf.test(file.name))
		{
			var reader = new FileReader();
			let filename = JSON.parse(JSON.stringify(file.name));
			let that = this;
			reader.onload = function(evt) { GfxContainer.prototype.LoadCompleted.call(that, evt, filename); };
			reader.readAsText(file);
		}
		else
		{
			this.ReportError("Unsupported file type: " + file.name);
		}
	}
}

//-------------------------------------------------------------------------------------
//	Build Container
//-------------------------------------------------------------------------------------
GfxContainer.prototype.BuildContainer = function()
{
	let that = this;
	var containerDiv = this.containerDiv;
	
	// Create the DOM elements and set their classes
	var rootDiv = document.createElement("div");
	rootDiv.classList.add("gfxContainer-root");

	var canvasDiv = document.createElement("div");
	canvasDiv.classList.add("gfxContainer-canvasDiv");
	
	var canvas = document.createElement("canvas");
	canvas.classList.add("gfxContainer-canvas");
	
	var statusArea = document.createElement("div");
	statusArea.classList.add("gfxContainer-statusArea");
	statusArea.classList.add("gfxContainer-hideElement");	
	
	let mouseActions = ["mousedown", "mousemove", "mouseup", "mouseleave"];
	for (var i = 0; i < mouseActions.length; i++)
	{
		let action = mouseActions[i];
		canvas.addEventListener(action, function (evt)  { GfxContainer.prototype.HandleMouse.call(that, evt, action); });
	}
	
	var controlsDiv = document.createElement("div");
	controlsDiv.classList.add("gfxContainer-controls");
	
	// Assemble the heirarchy
	canvasDiv.appendChild(canvas);
	canvasDiv.appendChild(statusArea);
	
	rootDiv.appendChild(canvasDiv);
	rootDiv.appendChild(controlsDiv);
	
	// Remember the elements we will access frequently
	this.canvas = canvas;
	this.controlsDiv = controlsDiv;
	this.statusArea = statusArea;
	
	// Install in the page
	containerDiv.appendChild(rootDiv);
	
	// We might want to add a config option for this if we want to make support for
	// resizing canvases optional
	window.addEventListener("resize", function() { GfxContainer.prototype.HandleResize.call(that)} ) ;

	canvas.addEventListener("wheel", function(evt) { GfxContainer.prototype.HandleMousewheel.call(that,evt)} /*,  {passive: true}*/);
	
	this.UpdateCanvasDimensions();
}

//-------------------------------------------------------------------------------------
//	Make Control ID
//-------------------------------------------------------------------------------------
GfxContainer.prototype.MakeControlID = function(groupIdx, controlIdx)
{
	return "ID_" + groupIdx + "_" + controlIdx;
}

//-------------------------------------------------------------------------------------
//	Remove UI
//-------------------------------------------------------------------------------------
GfxContainer.prototype.RemoveUI = function()
{
	this.controlsDiv.innerHTML = "";
}

//-------------------------------------------------------------------------------------
//	ToggleTwoColumn
//-------------------------------------------------------------------------------------
GfxContainer.prototype.AdjustTwoColumn = function(action = "disable")
{
	if (action == "toggle")
		this.twoColumn = !this.twoColumn;
	else if (action == "enable")
		this.twoColumn = true;
	else
		this.twoColumn = false;

	this.EnableTwoColumnControls(this.twoColumn);
	
	this.HandleResize();
}

//-------------------------------------------------------------------------------------
//	EnableTwoColumnControls
//-------------------------------------------------------------------------------------
GfxContainer.prototype.EnableTwoColumnControls = function(enable)
{
	let classBases = ["gfxContainer-controls", "gfxContainer-canvasDiv", "gfxContainer-controlGroups", "gfxContainer-controlGroup"];
	let classSuffix = "-twoColumn";
	
	for (var i = 0; i < classBases.length; i++)
	{
		let base = classBases[i];
		let twoColumn = base + classSuffix;
		let eList = this.containerDiv.getElementsByClassName(base);
		
		for (var j = 0; j < eList.length; j++)
		{
			if (enable)
				eList[j].classList.add(twoColumn);
			else
				eList[j].classList.remove(twoColumn);
		}
	}
}


//-------------------------------------------------------------------------------------
//	SVG Components
//-------------------------------------------------------------------------------------

// Placing an SVG element after an element 
//	var newSvg = document.getElementById('myDiv');
//	newSvg.outerHTML += '<svg xmlns="http://www.w3.org/2000/svg" style="position:absolute;z-index:10;margin:0;padding:0;top:0em;left:0.5em" onclick="go()" width="100" height="100"><circle cx="40" cy="40" r="40" stroke="red" stroke-width="4" fill="blue" /></svg>';

//let buttonSVG = '<svg xmlns="http://www.w3.org/2000/svg" style="position:absolute;z-index:10;margin:0;padding:0;top:0em;left:0.5em" onclick="go()" width="100" height="100"><circle cx="40" cy="40" r="40" stroke="red" stroke-width="4" fill="blue" /></svg>';
//let buttonSVG = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"><circle cx="10" cy="10" r="7" stroke="#808080" stroke-width="1" fill="none"/></svg>';

// SVG Prefix and suffix (note the hard-coded size)
let svgPrefix = '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="20">';
let svgSuffix = '</svg>';

// SVG Path prefix and suffix (note the hard-coded stroke color, stroke width, and fill)
let svgPathPrefix = '<path d="';
let svgPathSuffix = '" stroke="#808080" stroke-width="1" fill="none"/> ';

// Example SVG graphic elements (circle, path)
//let svgPath1 = '<circle cx="10" cy="10" r="7" stroke="#808080" stroke-width="1" fill="none"/>';
//let svgPath3 = '<path d="M 3 3 l 3 0 l 0 14 l -3 0 l 0 -14 z" stroke="#808080" stroke-width="1" fill="none"/> ';

// Example SVG path data
//let svgPathData = "M 3 3 l 3 0 l 0 14 l -3 0 l 0 -14 z";
//let svgPathDataA = "M 3 3 a 3 3 0 0 1 3 -3 l 3 0 a 3 3 0 0 1 3 3 l 0 14 a 3 3 0 0 1 -3 3 l -3 0 a 3 3 0 0 1 -3 -3 z";
//let svgPathDataB = "M 5 5 q 0 -3 3 -3 l 3 0 q 3 0 3 3 l 0 8 q 0 3 -3 3 l -3 0 q -3 0 -3 -3 z";

// A single "column" as part of an SVG path 
//var svgColumnPathData_v1 = "q 0 -3 3 -3 l 1 0 q 3 0 3 3 l 0 7 q 0 3 -3 3 l -1 0 q -3 0 -3 -3 z"
//var svgColumnPathData_v2 = "q 0 -2 2 -2 l 4 0 q 2 0 2 2 l 0 9 q 0 2 -2 2 l -4 0 q -2 0 -2 -2 z m 5 0"
var svgColumnPathData    = "m 0 2 q 0 -2 2 -2 l 2 0 q 2 0 2 2 l 0 9 q 0 2 -2 2 l -2 0 q -2 0 -2 -2 z m 5 -2"
// Constructing the path data with moveTo and the columns
let svgPathDataC = "M 4 4 " + svgColumnPathData + " m 4 0 " + svgColumnPathData;
// Constructing a path with the prefix, data, and suffix
let svgPath = svgPathPrefix + svgPathDataC + svgPathSuffix;
// Constructing a complete SVG element
let buttonSVG = svgPrefix + svgPath + svgSuffix;

//-------------------------------------------------------------------------------------
//	Create UI
//-------------------------------------------------------------------------------------
GfxContainer.prototype.CreateUI = function()
{
	let that = this;
	var controlGroups = []; 
	
	// Get the control groups for the app
	if (this.gfxApp != undefined)
		controlGroups = controlGroups.concat(this.gfxApp.GetControlList());
	
	// For 3D
	if (this.appConfig.display3D)
		controlGroups = controlGroups.concat(GfxContainer.controlGroups3D);
		
	// Add the control groups for the container
	controlGroups = controlGroups.concat(GfxContainer.controlGroups);
	
	// Store the combined control group list to use in the control handler
	this.controlGroups = controlGroups;
	
	// Create two divs, one for the name and one for the control groups
	var cgTitleDiv = document.createElement("div");
	cgTitleDiv.classList.add("gfxContainer-controlsTitle");
	this.controlsDiv.appendChild(cgTitleDiv);
	
	var cgControlGroupsDiv = document.createElement("div");
	cgControlGroupsDiv.classList.add("gfxContainer-controlGroups");
	this.controlsDiv.appendChild(cgControlGroupsDiv);
	
	// Start by showing the name of the app currently installed
	var cgAppName = document.createElement("div");
	cgAppName.innerHTML = this.GetAppName();
	cgAppName.classList.add("gfxContainer-controlAppName");
	cgTitleDiv.appendChild(cgAppName);
	
	// Button to switch between one-column and two-column display
	var cgColumnButton = document.createElement("div");
	cgColumnButton.classList.add("gfxContainer-columnButton");
	cgColumnButton.dataset.gfxAction = "gfxToggleTwoColumn";
	cgColumnButton.addEventListener("click", function (evt) { GfxContainer.prototype.HandleButton.call(that, evt); });
	cgColumnButton.innerHTML = buttonSVG;
	cgTitleDiv.appendChild(cgColumnButton);
	
	// Iterate over the control groups
	for (var k = 0; k < controlGroups.length; k++)
	{
		let cg = controlGroups[k];
		
		// Create a div for the control group
		var cgDiv = document.createElement("div");
		cgDiv.classList.add("gfxContainer-controlGroup");
		
		// Add a group label
		var cgLabel = document.createElement("p");
		cgLabel.innerHTML = cg.label;
		cgLabel.classList.add("gfxContainer-controlGroupLabel");
		cgDiv.appendChild(cgLabel);
		
		// Iterate of the controls
		for (var i = 0; i < cg.controls.length; i++)
		{
			var c = cg.controls[i];
			
			var id = this.MakeControlID(k, i);
			
			if (c.type == "number")
			{
				var cgLabel = document.createElement("label");
				cgLabel.innerHTML = c.label;
				cgLabel.setAttribute("for", id);
				cgDiv.appendChild(cgLabel);
				
				var cgRange = document.createElement("input");
				cgRange.setAttribute("type", "range");
				cgRange.setAttribute("id", id);
				cgRange.min = c.min;
				cgRange.max = c.max;
				cgRange.step = c.step;
				cgRange.addEventListener("input", function (evt) { GfxContainer.prototype.HandleControl.call(that, evt); });
				cgDiv.appendChild(cgRange);
				
				if (c.entry == "yes")
				{
					var idEntry = id + "_Entry";
					var cgNumber = document.createElement("input");
					cgNumber.setAttribute("type", "number");
					cgNumber.setAttribute("id", idEntry);
					cgNumber.setAttribute("for", id);
					cgNumber.min = c.min;
					cgNumber.max = c.max;
					cgNumber.step = c.step;
					cgNumber.addEventListener("input", function (evt) { GfxContainer.prototype.HandleControl.call(that, evt); });
					cgNumber.dataset.pairedID = id;
					cgRange.dataset.pairedID = idEntry;
					cgDiv.appendChild(cgNumber);
				}
				
				cgDiv.appendChild(document.createElement("br"));
				
			}
			else if (c.type == "text")
			{
				if (c.label != undefined && c.label.length > 0)
				{
					var cgLabel = document.createElement("label");
					cgLabel.innerHTML = c.label;
					cgLabel.setAttribute("for", id);
					cgDiv.appendChild(cgLabel);
				}
				
				var cgField = document.createElement("input");
				cgField.setAttribute("id", id);
				cgField.addEventListener("input", function (evt) { GfxContainer.prototype.HandleControl.call(that, evt); });
				cgDiv.appendChild(cgField);
						
				cgDiv.appendChild(document.createElement("br"));
			}
			else if (c.type == "boolean")
			{
				var cgCheckbox = document.createElement("input");
				cgCheckbox.setAttribute("type", "checkbox");
				cgCheckbox.setAttribute("id", id);
				cgCheckbox.addEventListener("click", function (evt) { GfxContainer.prototype.HandleControl.call(that, evt); });
				cgDiv.appendChild(cgCheckbox);

				var cgLabel = document.createElement("label");
				cgLabel.setAttribute("for", id);
				cgLabel.innerHTML = c.label;
				cgLabel.classList.add("gfxContainer-checkBoxLabel");
				cgDiv.appendChild(cgLabel);

				cgDiv.appendChild(document.createElement("br"));
			}
			else if (c.type == "selection")
			{
				var cgLabel = document.createElement("label");
				cgLabel.setAttribute("for", id);
				cgLabel.innerHTML = c.label;
				cgDiv.appendChild(cgLabel);

				var cgSelect = document.createElement("select");
				cgSelect.setAttribute("id", id);
				
				for (var j = 0; j < c.options.length; j++)
				{
					var cgOption = document.createElement("option");
					cgOption.innerHTML = c.options[j].label;
					cgOption.value = c.options[j].value;
					cgSelect.appendChild(cgOption);
				}
				cgSelect.addEventListener("input", function (evt) { GfxContainer.prototype.HandleControl.call(that, evt); });
				cgDiv.appendChild(cgSelect);

				cgDiv.appendChild(document.createElement("br"));
			}
			else if (c.type == "button")
			{
				var cgButton = document.createElement("button");
				cgButton.innerHTML = c.label;
				cgButton.dataset.gfxAction = c.action;
				cgButton.addEventListener("click", function (evt) { GfxContainer.prototype.HandleButton.call(that, evt); });
				cgDiv.appendChild(cgButton);
			}
			else if (c.type == "file")
			{
				var cgFile = document.createElement("input");
				cgFile.setAttribute("type", "file");
				cgFile.dataset.gfxAction = c.action;
				cgFile.addEventListener('change', function (evt, loadInfo) { GfxContainer.prototype.LoadFile.call(that, evt, loadInfo); });
				cgDiv.appendChild(cgFile);
			}
			else
			{
				var e = document.createElement("p");
				e.innerHTML = c.label;
				cgDiv.appendChild(e);
			}
		}
		
		cgControlGroupsDiv.appendChild(cgDiv);
	}
		
	if (1 /* info */)
	{
		// Create a div for the control group
		var cgDiv = document.createElement("div");
		cgDiv.classList.add("gfxContainer-controlGroup");
	
		// Add a group label
		var cgLabel = document.createElement("p");
		cgLabel.innerHTML = "Design Info";
		cgLabel.classList.add("gfxContainer-controlGroupLabel");
		cgDiv.appendChild(cgLabel);
		
		var cgInfo = document.createElement("p");
		cgInfo.innerHTML = "...info...";
		cgInfo.classList.add("gfxContainer-info");
		this.infoElement = cgInfo;
		cgDiv.appendChild(cgInfo);
		
		cgControlGroupsDiv.appendChild(cgDiv);
	}
	
	// Add a close button if requested
	if (this.config.addCloseButton)
	{
		var cgButton = document.createElement("button");
		cgButton.innerHTML =  (typeof(this.config.addCloseButton) == "string") ? this.config.addCloseButton : "Close";
		cgButton.dataset.gfxAction = "gfxCloseContainer";
		cgButton.addEventListener("click", function (evt) { GfxContainer.prototype.HandleButton.call(that, evt); });
		cgButton.classList.add("gfxContainer-closeButton");
		cgControlGroupsDiv.appendChild(cgButton);
	}
}

//-------------------------------------------------------------------------------------
//	Handle Control
//-------------------------------------------------------------------------------------
GfxContainer.prototype.HandleControl = function(evt)
{
	var id = evt.target.id;
	var group = undefined;
	var ctl = undefined;
	var value;
	
	// Parse the id that we built earlier
	var groupIdx = Number(id.split("_")[1]);
	var ctlIdx   = Number(id.split("_")[2]);

	// Get the group and control	
	if (this.controlGroups != undefined && groupIdx >= 0 && groupIdx < this.controlGroups.length)
	{
		group = this.controlGroups[groupIdx];
		
		if (group.controls != undefined && ctlIdx >= 0 && ctlIdx < group.controls.length)
			ctl = group.controls[ctlIdx];
	}
	
	if (ctl != undefined)
	{
		// Get the value according to the type of control
		if (ctl.type == "boolean")
			value = evt.target["checked"];
		else
			value = evt.target["value"];
		
		// Clean-up numbers
		if (ctl.type == "number")
			value = Number(value);
		
		// If this is a range or an input and it is paired with an input or a range
		// then update the other control
		if (evt.target.dataset.pairedID != undefined)
		{
			let e = this.controlsDiv.querySelector("#" + evt.target.dataset.pairedID);
			if (e != undefined)
				e.value = value;
		}
		
		// Update the property in either the container or the app
		if (group.owner == "container")
			this.SetContainerProperty(ctl.prop, value, ctl);
		else
			this.SetAppProperty(ctl.prop, value, ctl);
		
		// Create the gfx if we changed an app property 
		if (group.owner != "container")
			this.Generate();
		
		// If we updated a 3D view transform setting, then update the camera
		if (group.update3D)
			this.UpdateCamera();
			
		// Render
		this.Render();
		this.UpdateInfo();
	}
	else
	{
		console.log("handleControl: unknown control: " + evt.target.id);
	}
}

//-------------------------------------------------------------------------------------
//	Handle Button
//-------------------------------------------------------------------------------------
GfxContainer.prototype.HandleButton = function(evt)
{
	var action = evt.currentTarget.dataset.gfxAction;
	
	if (action == "gfxExportSVG")
		this.SaveAsSVG();

	else if (action == "gfxZoomToFit")
		this.ZoomTo("fit");

	else if (action == "gfxZoom100")
		this.ZoomTo("100%");

	else if (action == "gfxToggleTwoColumn")
		this.AdjustTwoColumn("toggle");

	else if (action == "gfxOpenFile")
	{
		// Handled by the LoadFile callback; should not get here
	}
	else if (action == "gfxCloseContainer")
		this.DoCloseContainer();

	else
	{
		console.log("GfxContainer.prototype.HandleButton: Button not handled, action:", action, "id:", evt.target.id  );
	}
}

//-------------------------------------------------------------------------------------
//	Do Close Container
//-------------------------------------------------------------------------------------
GfxContainer.prototype.DoCloseContainer = function(evt)
{
	if (this.config.closeCallback != undefined)
		this.config.closeCallback();
}

//-------------------------------------------------------------------------------------
//	Populate Controls
//-------------------------------------------------------------------------------------
GfxContainer.prototype.PopulateControls = function()
{
	for (var k = 0; (this.controlGroups != undefined) && k < this.controlGroups.length; k++)
	{
		let group = this.controlGroups[k];
		var value;
		
		for (var i = 0; i < group.controls.length; i++)
		{
			let ctl = group.controls[i];
			
			if (group.owner == "container")
				value = this.GetContainerProperty(ctl.prop);
			else
				value = this.GetAppProperty(ctl.prop);
				
			let id = this.MakeControlID(k, i);
			let e = this.controlsDiv.querySelector("#" + id);
			
			if (e != undefined)
			{
				if (ctl.type == "boolean")
					e["checked"] = value;
				else
					e["value"] = value;
			}
			
			if (ctl.entry == "yes")
			{
				let e = this.controlsDiv.querySelector("#" + id + "_Entry");
				if (e != undefined)
					e["value"] = value;
			}
		}
	}
	
	/*
	for (var i = 0; i < controlList.length; i++)
	{
		var ctl = controlList[i];
		var id = ctl.id;
		var e = document.getElementById(id);
		var v = (ctl.valueID != undefined) ? document.getElementById(ctl.valueID) : undefined;
		
		if (ctl.prop != undefined && ShapeSettings[ctl.prop] != undefined)
		{
			var value = (ctl.subprop == undefined) ?  ShapeSettings[ctl.prop] : ShapeSettings[ctl.prop][ctl.subprop];
			if (e != undefined)
				e.value = value;
			if (v != undefined)
				v.value = value;
		}
		
		if (id == "ID_Scale")
		{
			if (e != undefined)
				e.value = viewTransform.scale.x;
			if (v != undefined)
				v.value = viewTransform.scale.x;
		}
	}
	*/
}

//-------------------------------------------------------------------------------------
//	Zoom To Fit
//-------------------------------------------------------------------------------------
GfxContainer.prototype.ZoomTo = function(level)
{
	if (this.gfxApp != undefined)
	{
		var bounds = this.gfxApp.GetBounds();
		let margin = 10;
		let canvasBounds = {
			min:{x:margin, y:margin}, 
			max:{x:this.canvas.width - margin, y:this.canvas.height - margin}};

		var zoomLevel = undefined;
		
		if (level == "100%")
			zoomLevel = 1.0;
		else if (level == "fit")
			zoomLevel = undefined;
		else if (typeof(level) == "number")
			zoomLevel = level;
			
		this.viewTransform.CalcMappingTransform(bounds, canvasBounds, zoomLevel);
		this.Render();
	}
}

//-------------------------------------------------------------------------------------
//	getMousePos
//-------------------------------------------------------------------------------------
function getMousePos(aCanvas, evt)
{
	var rect = aCanvas.getBoundingClientRect();
	var mouseX = (evt.changedTouches ? evt.changedTouches[0].clientX : evt.clientX) - rect.left; //this.offsetLeft;
	var mouseY = (evt.changedTouches ? evt.changedTouches[0].clientY : evt.clientY) - rect.top; //this.offsetTop;
	var insetX = 1; // These match the border on the canvas. Hardcoding is less
	var insetY = 1; // expensive than computing the style
	return {
		x: mouseX - insetX,
		y: mouseY - insetY
	};
}

//-------------------------------------------------------------------------------------
//	Handle Mouse
//-------------------------------------------------------------------------------------
GfxContainer.prototype.HandleMouse = function(evt, mouseAction)
{
	var mousePt = getMousePos(this.canvas, evt);

	if (mouseAction == "mousedown")
	{
		this.mouseTracking.mousePt = mousePt;
		this.canvas.style.cursor = "grabbing";
	}
	else if (mouseAction == "mousemove")
	{
		if (this.mouseTracking.mousePt != undefined)
		{
			var delta = {x: mousePt.x - this.mouseTracking.mousePt.x, y: mousePt.y - this.mouseTracking.mousePt.y};

			this.viewTransform.offset.x += delta.x;
			this.viewTransform.offset.y += delta.y;
			
			this.Render();
			
			this.mouseTracking.mousePt.x = mousePt.x;
			this.mouseTracking.mousePt.y = mousePt.y;
		}
	}
	else if (mouseAction == "mouseup" || mouseAction == "mouseleave")
	{
		this.mouseTracking.mousePt = undefined;
		this.canvas.style.cursor = "grab";
	}

}

//-------------------------------------------------------------------------------------
//	Get App Name
//-------------------------------------------------------------------------------------
GfxContainer.prototype.GetAppName = function()
{
	return (this.gfxApp != undefined) ? this.gfxApp.GetName() : "[not set]";
}

//-------------------------------------------------------------------------------------
//	Get App Property
//-------------------------------------------------------------------------------------
GfxContainer.prototype.GetAppProperty = function(property)
{
	var value = 1;
	
	if (this.gfxApp != undefined)
		value = this.gfxApp.GetProperty(property, value);

	return value;
}

//-------------------------------------------------------------------------------------
//	Get Container Property
//-------------------------------------------------------------------------------------
GfxContainer.prototype.GetContainerProperty = function(property)
{
	var value = undefined;
	
	value = this.settings[property];
	
	return value;
}

//-------------------------------------------------------------------------------------
//	Set App Property
//-------------------------------------------------------------------------------------
GfxContainer.prototype.SetAppProperty = function(property, value)
{
	if (this.gfxApp != undefined)
		this.gfxApp.SetProperty(property, value);
}

//-------------------------------------------------------------------------------------
//	Set Container Property
//-------------------------------------------------------------------------------------
GfxContainer.prototype.SetContainerProperty = function(property, value)
{
	this.settings[property] = value;
}

//------------------------------------------------------------------------------
//	Update Canvas Dimensions
//------------------------------------------------------------------------------
GfxContainer.prototype.UpdateCanvasDimensions = function()
{
	if (this.canvas != undefined)
	{
		let c = this.canvas;
		
		c.style.width  = c.clientWidth;
		c.style.height = c.clientHeight;
		c.width  = c.clientWidth;
		c.height = c.clientHeight;
	}
}

//------------------------------------------------------------------------------
//	Handle Resize
//------------------------------------------------------------------------------
GfxContainer.prototype.HandleResize = function()
{
	this.UpdateCanvasDimensions();
	this.Render();
}

//------------------------------------------------------------------------------
//	Handle Mousewheel
//------------------------------------------------------------------------------
GfxContainer.prototype.HandleMousewheel = function(evt)
{
	var mousePt = getMousePos(this.canvas, evt);
	
	//if (evt.metaKey || ignoreMetaKey())
	{
		var changePercent = 0.05;
		var lowerScaleLimit = 0.1; 
		var scaleChanged = this.viewTransform.AdjustScaleAroundBy(mousePt, (evt.deltaY > 0) ? changePercent : -changePercent, lowerScaleLimit);
	
		if (scaleChanged)
			this.Render();
	}
	//else
	//	ScreenDesigner.DisplayHoldCommandKeyBanner(sdCanvas.id);
}

//------------------------------------------------------------------------------
//	Initialize View Transform
//------------------------------------------------------------------------------
GfxContainer.prototype.InitializeViewTransform = function()
{
	var offset = {x: 0, y: 0 };
	var yscale = 1.0;
	
	if (this.appConfig.origin == "center" && this.canvas != undefined)
		offset = { x: this.canvas.width/2, y: this.canvas.height/2 };
	
	if (this.appConfig.ypositive == "up")
		yscale = -1.0;
		
	this.settings.yscale = yscale;
	
	// These should an API on Transform()
	this.viewTransform.scale.y = yscale;
	this.viewTransform.offset.x = offset.x;
	this.viewTransform.offset.y = offset.y;
}
//------------------------------------------------------------------------------
//	Update Camera
//------------------------------------------------------------------------------
GfxContainer.prototype.UpdateCamera = function()
{
	var cameraSettings = {};
	
	cameraSettings.ax = this.settings.rotationX;
	cameraSettings.ay = this.settings.rotationY;
	cameraSettings.az = this.settings.rotationZ;

	this.viewProjection.UpdateCamera(cameraSettings);
}

//------------------------------------------------------------------------------
//	Handle Resize
//------------------------------------------------------------------------------
GfxContainer.prototype.ReportError = function(errorStr)
{
	if (this.config.reportError != undefined)
		this.config.reportError(errorStr);
}

//-------------------------------------------------------------------------------------
//	SetFileNamespace
//-------------------------------------------------------------------------------------
GfxContainer.prototype.SetFileNamespace = function(companyName, appName, dataName, dataId)
{
	this.fileNamespace.companyName = companyName;
	this.fileNamespace.appName = appName;
	this.fileNamespace.dataName = dataName;
	this.fileNamespace.dataId = dataId;
}

//------------------------------------------------------------------------------
//	Finalize Run
//------------------------------------------------------------------------------
GfxContainer.prototype.FinalizeRun = function()
{
	if (this.gfxApp != undefined)
	{
		if (this.appConfig.initialZoom != undefined)
			this.ZoomTo(this.appConfig.initialZoom);
	}
}

//-------------------------------------------------------------------------------------
//	Run App
//
//		Called from onLoad
//-------------------------------------------------------------------------------------
GfxContainer.prototype.Run = function()
{
	this.CreateUI();
	this.PopulateControls();
	this.InitializeViewTransform();
	this.Generate();
	this.UpdateInfo();
	this.Render();
	this.FinalizeRun();
}

//-------------------------------------------------------------------------------------
//	Public
//-------------------------------------------------------------------------------------
export { GfxContainer };
